"use client"

import { renderToString } from "katex"

import type { StringWithHTML } from "@/../types"

import "katex/dist/katex.min.css"

import type { Term } from "@/generated/course-material-api/types.generated"
import { omitUndefined } from "@/shared-module/common/utils/nullability"
import { decodeHtmlEntities } from "@/utils/course-material/htmlEntities"
import { sanitizeCourseMaterialHtml } from "@/utils/course-material/sanitizeCourseMaterialHtml"

const LATEX_REGEX = /\[latex\](.*?)\[\/latex\]/g
const LATEX_CITE_REGEX = /\\cite(?:\[([^\]]*)\])?(?:\[([^\]]*)\])?{(.*?)}/g
const HTML_ESCAPED_AMPERSAND = "&amp;"
const AMPERSAND_CHAR = "&"
const HTML_MIME_TYPE = "text/html"
const KATEX_OUTPUT_FORMAT = "htmlAndMathml"
const REGEX_MODE = "gu"

// A term edge that is a word character must not touch another one. The MCP glossary tool
// (whole_word_matches in services/headless-lms/mcp/src/text.rs) uses the same rule.
const WORD_CHAR = "[\\p{Alphabetic}\\p{N}_]"
const WORD_CHAR_REGEX = new RegExp(`^${WORD_CHAR}$`, "u")
const NOT_AFTER_WORD_CHAR = `(?<!${WORD_CHAR})`
const NOT_BEFORE_WORD_CHAR = `(?!${WORD_CHAR})`
// Simple FIFO cache for compiled glossary term regexes; Map preserves insertion order.
const TERM_REGEX_CACHE = new Map<string, RegExp>()
const TERM_REGEX_CACHE_MAX_SIZE = 100

/** Escapes regex metacharacters in a string so it can be used literally in a RegExp. */
const escapeRegex = (value: string): string => value.replaceAll(/[.*+?^${}()|[\]\\]/g, "\\$&")

const ANCHOR_TAG_NAME = "A"

const isDescendantOfAnchor = (node: Node): boolean => {
  let current = node.parentElement
  while (current) {
    if (current.tagName === ANCHOR_TAG_NAME) {
      return true
    }
    current = current.parentElement
  }
  return false
}

/**
 * Rejects text nodes inside `<a>` elements so the glossary tooltip's trigger `<button>` never
 * gets portaled inside a link's visible text, which would nest an interactive element inside
 * another one and break click/focus/screen-reader behavior.
 */
const glossaryTextNodeFilter: NodeFilter = {
  acceptNode: (node) =>
    isDescendantOfAnchor(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
}

const getTermRegex = (term: string): RegExp => {
  let regex = TERM_REGEX_CACHE.get(term)
  if (!regex) {
    if (TERM_REGEX_CACHE.size >= TERM_REGEX_CACHE_MAX_SIZE) {
      const oldestKey = TERM_REGEX_CACHE.keys().next().value
      if (oldestKey !== undefined) {
        TERM_REGEX_CACHE.delete(oldestKey)
      }
    }
    const characters = Array.from(term)
    const startsWithWordChar = WORD_CHAR_REGEX.test(characters[0] ?? "")
    const endsWithWordChar = WORD_CHAR_REGEX.test(characters.at(-1) ?? "")
    regex = new RegExp(
      (startsWithWordChar ? NOT_AFTER_WORD_CHAR : "") +
        escapeRegex(term) +
        (endsWithWordChar ? NOT_BEFORE_WORD_CHAR : ""),
      REGEX_MODE,
    )
    TERM_REGEX_CACHE.set(term, regex)
  }
  return regex
}

const SPAN_TAG = "span"
const DATA_GLOSSARY_ID_ATTR = "data-glossary-id"
let domParser: DOMParser | null = null

/** Returns a DOMParser instance, lazily created to avoid SSR issues. */
const getDomParser = (): DOMParser => {
  if (domParser) {
    return domParser
  }
  if (typeof DOMParser === "undefined") {
    throw new TypeError("DOMParser is not available in this environment.")
  }
  domParser = new DOMParser()
  return domParser
}

const DATA_CITATION_ID_ATTR = "data-citation-id"
const DATA_CITATION_PRENOTE_ATTR = "data-citation-prenote"
const DATA_CITATION_POSTNOTE_ATTR = "data-citation-postnote"
const HTML_ENTITY_QUOT = "&quot;"
const HTML_ENTITY_NBSP = "&nbsp;"
const QUOTE_REGEX = /"/g
const TILDE_REGEX = /~/g

/** Escapes citation-related text for safe use in HTML attributes. */
const escapeCitationText = (value: string): string =>
  value.replace(QUOTE_REGEX, HTML_ENTITY_QUOT).replace(TILDE_REGEX, HTML_ENTITY_NBSP)

/**
 * Escapes a citation key for a double-quoted HTML attribute. Unlike escapeCitationText it omits the
 * tilde -> &nbsp; display transform (a prenote/postnote display convention), so the value
 * round-trips: reading node.dataset.citationId back yields the original key and matches the keys
 * extracted from the block tree.
 */
const escapeCitationId = (value: string): string => value.replace(QUOTE_REGEX, HTML_ENTITY_QUOT)

/** Finds all whole-word matches of term in text; returns index and length for each. */
export const findTermMatches = (
  text: string,
  term: string,
): { index: number; length: number }[] => {
  const regex = getTermRegex(term)
  regex.lastIndex = 0
  const matches: { index: number; length: number }[] = []
  let match
  while ((match = regex.exec(text)) !== null) {
    matches.push({ index: match.index, length: match[0].length })
  }
  return matches
}

/** Splits a text node at match positions and inserts span elements with data-glossary-id. */
export const replaceTextNodeWithGlossarySpans = (
  doc: Document,
  textNode: Text,
  matches: { index: number; length: number }[],
  glossaryId: string,
): void => {
  const text = textNode.textContent ?? ""
  // oxlint-disable-next-line typescript/no-non-null-assertion -- split text node is mounted, so it always has a parent
  const parent = textNode.parentNode!
  const fragment = doc.createDocumentFragment()
  let lastIndex = 0

  for (const m of matches) {
    if (m.index > lastIndex) {
      fragment.append(doc.createTextNode(text.slice(lastIndex, m.index)))
    }
    // Empty span is a mounting point for the glossary tooltip portal; the user-visible
    // text is rendered later by the React tooltip component rather than being kept here.
    const span = doc.createElement(SPAN_TAG)
    span.setAttribute(DATA_GLOSSARY_ID_ATTR, glossaryId)
    fragment.append(span)
    lastIndex = m.index + m.length
  }

  if (lastIndex < text.length) {
    fragment.append(doc.createTextNode(text.slice(lastIndex)))
  }

  parent.replaceChild(fragment, textNode)
}

/**
 *
 * @param data HTML-content from the server
 * @returns HTML as string in which "[latex] ... [/latex]" will be replaced with katex
 */
const convertToLatex = (data: string) => {
  let count = 0
  const converted = data.replace(LATEX_REGEX, (_, latex) => {
    // Convert ampersand back to special symbol. This is needed e.g. in matrices
    const processed = latex.replaceAll(HTML_ESCAPED_AMPERSAND, AMPERSAND_CHAR)
    count++
    return renderToString(processed, {
      throwOnError: false,
      output: KATEX_OUTPUT_FORMAT,
    })
  })

  return { count, converted }
}

/** Parses glossary terms out of HTML and inserts span markers into text nodes.
 * Earlier items in the glossary array win when terms overlap, because matches
 * are removed from the DOM before later terms are processed.
 */
const parseGlossary = (data: string, glossary: Term[]): { parsedText: string; terms: Term[] } => {
  const usedGlossary: Term[] = []

  if (glossary.length === 0) {
    return { parsedText: data, terms: usedGlossary }
  }

  const doc = getDomParser().parseFromString(data, HTML_MIME_TYPE)

  for (const item of glossary) {
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, glossaryTextNodeFilter)
    const textNodes: Text[] = []
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text)
    }

    for (const textNode of textNodes) {
      const matches = findTermMatches(textNode.textContent ?? "", item.term)
      if (matches.length === 0) {
        continue
      }
      usedGlossary.push(item)
      replaceTextNodeWithGlossarySpans(doc, textNode, matches, item.id)
    }
  }

  if (usedGlossary.length === 0) {
    return { parsedText: data, terms: usedGlossary }
  }

  return { parsedText: doc.body.innerHTML, terms: usedGlossary }
}

export interface CitationMatch {
  citationKey: string
  prenote?: string
  postnote?: string
}

/**
 * Applies the single-bracket rule of \cite: `\cite[x]{k}` means x is the POSTNOTE (not the
 * prenote), while `\cite[a][b]{k}` means a=prenote, b=postnote. Shared by citation rendering and
 * citation extraction so the rendered markers and the collected reference list can't diverge.
 */
const normalizeCitationNotes = (
  prenote: string | undefined,
  postnote: string | undefined,
): { prenote: string | undefined; postnote: string | undefined } => {
  if (prenote && postnote === undefined) {
    return { prenote: undefined, postnote: prenote }
  }
  return { prenote, postnote }
}

/**
 * Extracts all \cite{...} occurrences from a raw text/HTML string, in document order.
 *
 * Shares LATEX_CITE_REGEX and the prenote/postnote rule with parseCitation so the citations
 * collected for the reference list and their numbering stay in lockstep with what is rendered.
 * [latex]...[/latex] regions are removed first because parseText converts LaTeX before parsing
 * citations, so a \cite inside a latex block is consumed by KaTeX and never becomes a citation.
 */
export const extractCitationsFromText = (text: string | null | undefined): CitationMatch[] => {
  if (!text) {
    return []
  }
  const withoutLatex = text.replace(LATEX_REGEX, "")
  // Fresh RegExp so the module-level global regex's lastIndex isn't shared across calls.
  const regex = new RegExp(LATEX_CITE_REGEX.source, LATEX_CITE_REGEX.flags)
  const matches: CitationMatch[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(withoutLatex)) !== null) {
    const [, prenote, postnote, citationId] = match
    const notes = normalizeCitationNotes(prenote, postnote)
    matches.push({
      // The CMS stores a key like `a&b` as `a&amp;b`, while data-citation-id and the reference's
      // citation_key hold it decoded.
      citationKey: decodeHtmlEntities(citationId ?? ""),
      ...omitUndefined({ prenote: notes.prenote, postnote: notes.postnote }),
    })
  }
  return matches
}

/** Parses LaTeX \\cite commands in the raw HTML string into span markers.
 * This runs before DOM-based glossary parsing and assumes citation commands
 * appear only in text content, not inside HTML attribute values.
 */
const parseCitation = (data: string) => {
  const converted = data.replace(
    LATEX_CITE_REGEX,

    (_, prenote, postnote, citationId) => {
      const notes = normalizeCitationNotes(prenote, postnote)

      const prenoteAttr = notes.prenote
        ? ` ${DATA_CITATION_PRENOTE_ATTR}="${escapeCitationText(notes.prenote)}"`
        : ""
      const postnoteAttr = notes.postnote
        ? ` ${DATA_CITATION_POSTNOTE_ATTR}="${escapeCitationText(notes.postnote)}"`
        : ""
      const escapedCitationId = escapeCitationId(citationId ?? "")
      return `<${SPAN_TAG} ${DATA_CITATION_ID_ATTR}="${escapedCitationId}"${prenoteAttr}${postnoteAttr}></${SPAN_TAG}>`
    },
  )
  return converted
}

const parseText = (
  content: string | undefined | StringWithHTML,
  terms: Term[],
  { glossary = true }: { glossary?: boolean } = {},
) => {
  const { count, converted: parsedLatex } = convertToLatex(content ?? "")
  const parsedCitation = parseCitation(parsedLatex)

  let parsedText = parsedCitation
  let glossaryEntries: Term[] = []

  if (glossary) {
    const { parsedText: glossaryParsedText, terms: usedGlossary } = parseGlossary(
      parsedCitation,
      terms ?? [],
    )
    parsedText = glossaryParsedText
    const uniqueTerms = new Map<string, Term>()
    usedGlossary.forEach((term) => {
      if (!uniqueTerms.has(term.id)) {
        uniqueTerms.set(term.id, term)
      }
    })
    glossaryEntries = Array.from(uniqueTerms.values())
  }

  const hasCitationsOrGlossary = parsedLatex !== parsedText

  // Sanitation always needs to be the last step because otherwise we might accidentally introduce injection attacks with our custom parsing and modifications to the string
  parsedText = sanitizeCourseMaterialHtml(parsedText)
  return { count, parsedText, hasCitationsOrGlossary, glossaryEntries }
}

export { parseText }
